import { ArrowLeftIcon, SearchIcon } from "@chakra-ui/icons";
import {
  Box,
  Button,
  Flex,
  FormLabel,
  Heading,
  Icon,
  Input,
  InputGroup,
  InputLeftElement,
  SimpleGrid,
  Text,
  useColorModeValue,
  VStack,
} from "@chakra-ui/react";
import { Field, Form, Formik } from "formik";
import { useEffect, useMemo, useState } from "react";
import { BiLowVision, BiShow } from "react-icons/bi";
import { FaBookOpen } from "react-icons/fa";
import { useDispatch, useSelector } from "react-redux";
import { getAllSubjectsRequest } from "../../modules/admin-modules/subjects/Actions";
import { loginUserAction } from "../../modules/auth/Actions";
import PublicLayout from "../public/Layout/layout";
import { useNavigate } from "react-router-dom";

const LoginForm = (props) => {
  const dispatch = useDispatch();
  const isLoading = useSelector((state) => state.auth.isLoading);
  const [inputType, setInputType] = useState("password");
  const subjects = useSelector((state) => state.subjects.subjects);
  const [currentStep, setCurrentStep] = useState(1); // 1: Subject selection, 2: Login form
  const [selectedSubject, setSelectedSubject] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const navigate = useNavigate();
  useEffect(() => {
    dispatch(getAllSubjectsRequest());
  }, []);

  // Filter subjects based on search
  const filteredSubjects = useMemo(() => {
    return subjects.filter((subject) => {
      const matchesSearch = subject.name
        .toLowerCase()
        .includes(searchTerm.toLowerCase());
      return matchesSearch;
    });
  }, [subjects, searchTerm]);

  const validateUserName = (value) => {
    let error;
    if (!value) {
      error = "Required";
    }
    return error;
  };

  const validatePassword = (value) => {
    let error;
    if (!value) {
      error = "Required";
    }
    return error;
  };

  const handleSubjectSelect = (subject) => {
    setSelectedSubject(subject);
    setCurrentStep(2);
  };

  const handleBackToSubjects = () => {
    setCurrentStep(1);
    setSelectedSubject(null);
  };

  const bgColor = useColorModeValue("white", "gray.800");
  const borderColor = useColorModeValue("gray.200", "gray.600");
  const baseUrl = "https://api.b-study.online/";

  // Step 1: Subject Selection
  const renderSubjectSelection = () => (
    <VStack spacing={6} align="stretch" w="100%">
      <Heading textAlign="center" mb="6">
        Choose Your Subject
      </Heading>

      {/* Search Bar */}
      <Box>
        <InputGroup>
          <InputLeftElement pointerEvents="none">
            <SearchIcon color="gray.400" />
          </InputLeftElement>
          <Input
            placeholder="Search subjects..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            size="lg"
            borderRadius="lg"
            border="1px"
            borderColor="gray.300"
            _focus={{
              borderColor: "orange.500",
              boxShadow: "0 0 0 1px var(--chakra-colors-orange-500)",
            }}
          />
        </InputGroup>
      </Box>

      {/* Results Count */}
      <Text fontSize="sm" color="gray.600">
        {filteredSubjects.length} subject
        {filteredSubjects.length !== 1 ? "s" : ""} found
      </Text>

      {/* Subject List */}
      <Box>
        <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} spacing={4}>
          {filteredSubjects.map((subject) => (
            <Button
              key={subject.id}
              onClick={() => handleSubjectSelect(subject)}
              variant="outline"
              height="auto"
              p={6}
              textAlign="left"
              borderRadius="lg"
              borderWidth="2px"
              borderColor="gray.200"
              bg={bgColor}
              _hover={{
                borderColor: "orange.300",
                bg: "orange.50",
                shadow: "md",
              }}
              _active={{
                transform: "scale(0.98)",
              }}
            >
              <Flex justify="space-between" align="flex-start" width="100%">
                <VStack align="start" spacing={2} flex={1} minW={0}>
                  <Text
                    fontSize="md"
                    fontWeight="semibold"
                    wordBreak="break-word"
                    width="100%"
                    whiteSpace="normal"
                    overflowWrap="break-word"
                  >
                    {subject.name}
                  </Text>
                </VStack>

                {subject.logo ? (
                  <Box
                    width="80px"
                    height="80px"
                    borderRadius="lg"
                    overflow="hidden"
                    bg="gray.100"
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                    ml={4}
                    flexShrink={0}
                  >
                    <img
                      src={baseUrl + subject.logo}
                      alt={subject.name}
                      style={{
                        width: "100%",
                        height: "100%",
                        objectFit: "cover",
                      }}
                    />
                  </Box>
                ) : (
                  <Box
                    width="80px"
                    height="80px"
                    borderRadius="lg"
                    overflow="hidden"
                    bg="gray.100"
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                    ml={4}
                    flexShrink={0}
                  >
                    <img
                      src={"/logo.png"}
                      alt={subject.name}
                      style={{
                        width: "100%",
                        height: "100%",
                        objectFit: "cover",
                      }}
                    />
                  </Box>
                )}
              </Flex>
            </Button>
          ))}
        </SimpleGrid>

        {filteredSubjects.length === 0 && (
          <VStack py={12} spacing={4}>
            <Icon as={FaBookOpen} boxSize={12} color="gray.400" />
            <Text fontSize="lg" fontWeight="medium">
              No subjects found
            </Text>
            <Text color="gray.600">
              Try adjusting your search or filter criteria
            </Text>
          </VStack>
        )}
      </Box>
    </VStack>
  );

  // Step 2: Login Form
  const renderLoginForm = () => (
    <Formik
      initialValues={{
        phone: "",
        password: "",
        subject_id: selectedSubject?.id || null,
      }}
      onSubmit={(values) => {
        let formData = new FormData();
        formData.append("phone", values.phone);
        formData.append("password", values.password);
        formData.append("subject_id", values.subject_id);

        dispatch(loginUserAction(formData));
      }}
    >
      {({ errors, touched, values, setFieldValue }) => {
        // Update form value when subject is selected
        if (selectedSubject && values.subject_id !== selectedSubject.id) {
          setFieldValue("subject_id", selectedSubject.id);
        }

        return (
          <Form>
            <VStack spacing={6} align="stretch" w="100%">
              {/* Back Button and Header */}
              <Flex justify="space-between" align="center">
                <Button
                  leftIcon={<ArrowLeftIcon />}
                  variant="ghost"
                  onClick={handleBackToSubjects}
                  color="gray.600"
                  _hover={{ bg: "gray.100" }}
                >
                  Back to Subjects
                </Button>
                <Heading size="lg">Login</Heading>
                <Box w="40px" /> {/* Spacer for centering */}
              </Flex>

              {/* Selected Subject Display */}
              {selectedSubject && (
                <Box
                  p={4}
                  bg="orange.50"
                  border="1px"
                  borderColor="orange.200"
                  borderRadius="lg"
                >
                  <Text fontSize="sm" color="orange.700" fontWeight="medium">
                    Selected Subject: {selectedSubject.name}
                  </Text>
                </Box>
              )}

              {/* Phone Input */}
              <Box>
                <Flex alignItems="center">
                  <FormLabel
                    htmlFor="phone"
                    className="form-label"
                    color="main_1"
                    fontSize="26px"
                  >
                    Phone
                  </FormLabel>
                </Flex>

                <Field
                  style={{
                    width: "100%",
                    padding: "10px",
                    objectFit: "contain",
                    borderRadius: "10px",
                    boxShadow: "inset 0 0 16px 0 rgba(0, 0, 0, 0.16)",
                    border: "solid 1px #010038",
                    backgroundColor: "#fff",
                  }}
                  name="phone"
                  validate={validateUserName}
                />
                {errors.phone && touched.phone && (
                  <Text color="red">{errors.phone}</Text>
                )}
              </Box>

              {/* Password Input */}
              <Box position="relative">
                <Flex alignItems="center">
                  <FormLabel
                    htmlFor="password"
                    className="form-label"
                    color="main_1"
                    fontSize="26px"
                  >
                    Password
                  </FormLabel>
                </Flex>

                <Field
                  style={{
                    padding: "10px",
                    objectFit: "contain",
                    borderRadius: "10px",
                    boxShadow: "inset 0 0 16px 0 rgba(0, 0, 0, 0.16)",
                    border: "solid 1px #010038",
                    backgroundColor: "#fff",
                    width: "100%",
                  }}
                  name="password"
                  validate={validatePassword}
                  type={inputType}
                />

                <Icon
                  position="absolute"
                  right="10px"
                  top="50px"
                  fontSize="33px"
                  color="main_1"
                  as={inputType == "password" ? BiShow : BiLowVision}
                  onClick={() =>
                    inputType == "password"
                      ? setInputType("text")
                      : setInputType("password")
                  }
                />
                {errors.password && touched.password && (
                  <Text color="red" w="180px" textAlign="justify">
                    {errors.password}
                  </Text>
                )}
              </Box>

              {/* Login Button */}
              <Button
                type="submit"
                w="250px"
                p="22px"
                alignSelf="center"
                isLoading={isLoading}
                bg="main_1"
                color="white"
                mt="20px"
              >
                Login
              </Button>

              <Button
                onClick={() => navigate("/signup")}
                mt="20px"
                w="250px"
                p="22px"
                alignSelf="center"
              >
               SignUp
              </Button>
            </VStack>
          </Form>
        );
      }}
    </Formik>
  );

  return (
    <PublicLayout color="main_1" bg="white" register={false}>
      <Flex
        flexDirection="column"
        alignItems="center"
        my="50px"
        w="100%"
        h="100%"
      >
        <Box
          sx={{
            width: { base: "95%", md: currentStep === 1 ? "90%" : "40%" },
            maxWidth: currentStep === 1 ? "1200px" : "500px",
          }}
        >
          {currentStep === 1 ? renderSubjectSelection() : renderLoginForm()}
        </Box>
      </Flex>
    </PublicLayout>
  );
};

export default LoginForm;